<template>
  <header class="navbar app-topbar">
    <div class="topbar-actions">
      <!-- Shown only on pages that can reload their own data (App_Shell passes
           the page's @refresh handler through). -->
      <button
        v-if="onRefresh"
        type="button"
        class="topbar-icon-btn"
        :title="refreshing ? 'Refreshing…' : 'Refresh'"
        aria-label="Refresh"
        :disabled="refreshing"
        @click="onRefresh()"
      >
        <font-awesome-icon icon="fa-solid fa-arrows-rotate" :spin="refreshing" />
      </button>
      <router-link
        :to="{ name: CHAT_ROUTE }"
        class="topbar-icon-btn topbar-support-link"
        :title="chatTitle"
        :aria-label="chatTitle"
      >
        <i class="pi pi-comments"></i>
        <span v-if="unreadChats" class="tk-badge topbar-badge">{{ unreadChats > 99 ? '99+' : unreadChats }}</span>
      </router-link>
      <router-link
        :to="{ name: TICKETS_ROUTE }"
        class="topbar-icon-btn topbar-support-link"
        :title="supportTitle"
        :aria-label="supportTitle"
      >
        <i class="pi pi-info-circle"></i>
        <span v-if="unreadTickets" class="tk-badge topbar-badge">{{ unreadTickets > 99 ? '99+' : unreadTickets }}</span>
      </router-link>

      <div class="dropdown">
        <button
          id="accountMenuButton"
          class="user-pill dropdown-toggle"
          type="button"
          data-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
        >
          <span class="user-pill__avatar">{{ initials }}</span>
          <span class="user-pill__name">{{ displayName }}</span>
          <i class="pi pi-chevron-down user-pill__chev" aria-hidden="true"></i>
        </button>
        <div class="dropdown-menu dropdown-menu-right account-menu" aria-labelledby="accountMenuButton">
          <div class="account-menu__head">
            <span class="user-pill__avatar account-menu__avatar">{{ initials }}</span>
            <div class="account-menu__meta">
              <span class="account-menu__name">{{ displayName }}</span>
              <span v-if="account && account.email" class="account-menu__email">{{ account.email }}</span>
            </div>
          </div>
          <div v-if="roleLabel" class="account-menu__role">
            <span class="dn-role-pill" :class="`dn-role-pill--${account.role}`">
              <i class="pi pi-shield" aria-hidden="true"></i>
              {{ roleLabel }}
            </span>
          </div>
          <div class="dropdown-divider"></div>
          <a class="dropdown-item account-menu__signout" role="button" @click="logout">
            <i class="pi pi-sign-out" aria-hidden="true"></i>
            Sign out
          </a>
        </div>
      </div>
    </div>
  </header>
</template>
<script>
import { CHAT_ROUTE, TICKETS_ROUTE } from '@/router/routes';
import { LOGOUT } from '@/store/action_types';
import { chatUnread } from '@/utils/chat_notifications';
import { accountDisplayName, accountInitials, formatRole } from '@/utils/display';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';

library.add(faArrowsRotate);

export default {
  props: {
    // Declared as props (not emits) so the button only renders when the page
    // actually handles a refresh.
    onRefresh: {
      type: Function,
      default: null,
    },
    refreshing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      TICKETS_ROUTE,
      CHAT_ROUTE,
    };
  },
  computed: {
    account() {
      return this.$store.getters.account.data;
    },
    displayName() {
      return accountDisplayName(this.account);
    },
    initials() {
      return accountInitials(this.account);
    },
    roleLabel() {
      return this.account?.role ? formatRole(this.account.role) : '';
    },
    // Tickets with unread messages — the badge on the support link.
    unreadTickets() {
      return Number(this.$store.getters.ticketUnread?.data?.tickets) || 0;
    },
    supportTitle() {
      if (!this.unreadTickets) return 'Support tickets';
      return `Support tickets — ${this.unreadTickets} with unread messages`;
    },
    // Conversations with unread messages — the badge on the chat link.
    unreadChats() {
      return chatUnread.conversations;
    },
    chatTitle() {
      if (!this.unreadChats) return 'Support chat';
      return `Support chat — ${this.unreadChats} with unread messages`;
    },
  },
  methods: {
    logout() {
      this.$store.dispatch(LOGOUT, { recordSignOut: true }).then(() => {
        this.$router.go();
      });
    },
  },
};
</script>
<style scoped>
.app-topbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  height: 4rem;
  background-color: var(--color-bg-canvas);
  border-bottom: 1px solid var(--color-border);
  padding: 0 1.25rem;
}

/* ---- Right actions ---- */
.topbar-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}

.topbar-icon-btn {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease, border-color .15s ease;
}

.topbar-icon-btn:hover {
  background-color: var(--color-bg-muted);
  color: var(--color-primary-hover);
  border-color: var(--color-border);
}

.topbar-icon-btn:disabled {
  cursor: wait;
  color: var(--color-primary-hover);
}

.topbar-icon-btn:focus-visible {
  box-shadow: var(--shadow-focus) !important;
}

.topbar-icon-btn .pi {
  font-size: 1rem;
}

.topbar-support-link {
  position: relative;
  text-decoration: none !important;
}

.topbar-badge {
  position: absolute;
  top: 2px;
  right: 1px;
  box-shadow: 0 0 0 2px var(--color-bg-canvas);
}

/* ---- User pill ---- */
.user-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 3px 12px 3px 4px;
  background-color: var(--color-bg-canvas);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  color: var(--color-text-primary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color .15s ease, box-shadow .15s ease, border-color .15s ease;
  margin-left: 4px;
}

.user-pill:hover {
  border-color: var(--color-primary-border);
  background-color: var(--color-bg-soft);
  box-shadow: var(--shadow-xs);
}

/* index.css clears the border of a focused button; the pill keeps its own. */
.user-pill:focus,
.user-pill:active,
.dropdown.show .user-pill {
  border-color: var(--color-primary-border) !important;
}

.user-pill:focus-visible {
  box-shadow: var(--shadow-focus) !important;
}

.user-pill__avatar {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  background-color: var(--color-primary);
  color: var(--color-text-on-primary);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.user-pill__name {
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-pill__chev {
  font-size: 10px;
  color: var(--color-text-muted);
  margin-left: 2px;
  transition: transform .15s ease;
}

.dropdown.show .user-pill__chev {
  transform: rotate(180deg);
}

/* Drop the default Bootstrap caret triangle */
.user-pill.dropdown-toggle::after,
.user-pill.dropdown-toggle::before {
  display: none !important;
}

.dropdown {
  position: relative;
}

/* ---- Account menu ---- */
.account-menu {
  min-width: 250px;
  margin-top: 6px;
}

.account-menu__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px 8px;
}

.account-menu__avatar {
  width: 36px;
  height: 36px;
  font-size: 0.78rem;
}

.account-menu__meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.account-menu__name {
  color: var(--color-text-primary);
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.account-menu__email {
  color: var(--color-text-muted);
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Lines up under the name, past the avatar. */
.account-menu__role {
  padding: 0 10px 8px 56px;
}

.account-menu .dropdown-divider {
  margin: 4px 0;
  border-top-color: var(--color-divider);
}

.account-menu .account-menu__signout {
  display: flex;
  align-items: center;
  gap: 9px;
  color: var(--color-danger) !important;
  font-weight: 500;
}

/* Outranks theme.css's brand wash on every .dropdown-item. */
.account-menu .account-menu__signout:hover,
.account-menu .account-menu__signout:active {
  background-color: #FEECEC !important;
  color: var(--color-danger) !important;
}

.account-menu__signout .pi {
  font-size: 0.8rem;
}
</style>
